import React, { useState, useRef, useEffect } from "react";
import "pagination-pattern/dist/foundation.css";
import chevronLeft from "styleguide/icons/ds2/chevronleftoutline.svg";

type PaginationProps = {
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  totalItems = 50,
  itemsPerPage = 20,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  // Don't create an array with all pages - only create visible pages on demand
  const getPageRange = (start: number, end: number) =>
    [...new Array(end - start + 1)].map((_, i) => start + i);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [visiblePages, setVisiblePages] = useState<number[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClick = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    setVisiblePages(getPageRange(1, Math.min(5, totalPages)));
  }, [totalPages]);

  useEffect(() => {
    const start = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const end = Math.min(start + 4, totalPages);
    setVisiblePages(getPageRange(start, end));
  }, [currentPage, totalPages]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const fromIndex = (currentPage - 1) * itemsPerPage + 1;
  const toIndex = Math.min(currentPage * itemsPerPage, totalItems);


  if (totalPages <= 1) {
    return null;
  }

  return (
    <div
      className="ds-pagination mobile:w-[10%]! tablet:w-[20%]! desktop:w-full!"
      data-ds-theme="vessel"
      data-ds-variant="navigation"
      data-ds-version="2"
      data-total-items={totalItems}
      data-items-per-page={itemsPerPage}
    >
      {/* COUNT */}
      <div className="ds-pagination__count mt-2!">
        <p role="status" aria-atomic="true">
          <span className="ds-pagination__fromToIndexTxt">
            {fromIndex} – {toIndex} of {totalItems} items
          </span>
        </p>
      </div>

      <nav className="ds-pagination__nav" role="navigation">
        {/* DESKTOP LIST */}
        <ul className="ds-pagination__list ds-pagination__desktop-list">
          {/* Prev */}
          <li
            className={`ds-pagination__item -prev ${currentPage === 1 ? "-disabled" : ""
              }`}
          >
            <button
              className="ds-pagination__link"
              disabled={currentPage === 1}
              onClick={() => {
                if (currentPage > 1) {
                  handleClick(currentPage - 1);
                }
              }}
            >
              <span className="screenreader-only">Previous page</span>
              <img
                alt="go to previous page"
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-back"
              />
            </button>
          </li>

          {/* PAGE NUMBERS */}
          {visiblePages.map((page) => (
            <li
              key={page}
              className={`ds-pagination__item -page ${currentPage === page ? "-active" : ""
                }`}
            >
              <button
                className="ds-pagination__link"
                onClick={() => handleClick(page)}
              >
                <span className="screenreader-only">
                  {currentPage === page ? "Selected page" : "Page"}
                </span>
                <span className="ds-pagination__pageIndex">{page}</span>
              </button>
            </li>
          ))}

          {/* Next */}
          <li
            className={`ds-pagination__item -next ${currentPage === totalPages ? "-disabled" : ""
              }`}
          >
            <button
              className="ds-pagination__link"
              disabled={currentPage === totalPages}
              onClick={() => {
                if (currentPage < totalPages) {
                  handleClick(currentPage + 1);
                }
              }}
            >
              <span className="screenreader-only">Next page</span>
              <img
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-forward"
                alt="go to next page"
              />
            </button>
          </li>
        </ul>

        {/* DESKTOP LABELS */}
        {visiblePages.length <= 2 ? (
          <div className="hidden lg-desktop:flex gap-4">
            <p className="back-label --desktop-label !static !inline-flex">
              Previous results
            </p>
            <p className="forward-label --desktop-label !static !inline-flex">
              Next results
            </p>
          </div>
        ) : (
          <div className="hidden lg-desktop:flex flex-col">
            <p className="back-label --desktop-label">Previous results</p>
            <p className="forward-label --desktop-label">Next results</p>
          </div>
        )}

        {/* MOBILE LIST */}
        <div className="ds-pagination__list ds-pagination__mobile-list">
          {/* Mobile Prev */}
          <li
            className={`ds-pagination__item -prev ${currentPage === 1 ? "-disabled" : ""
              }`}
          >
            <button
              className="ds-pagination__link"
              disabled={currentPage === 1}
              tabIndex={currentPage===1?-1:0}
              onClick={() => {
                handleClick(currentPage - 1);
              }}
            >
              <span className="screenreader-only">Previous page</span>
              <img
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-back"
                alt="go to previous page"
              />
            </button>
            <span>
              <p className="back-label mt-[10px]!" aria-hidden="true">
                Previous results
              </p>
            </span>
          </li>

          {/* MOBILE DROPDOWN */}
          <div
            ref={dropdownRef}
            className="ds-dropdown relative"
            data-ds-theme="vessel"
            data-ds-variant="basic"
            data-ds-version="2"
            data-list-type="option"
            data-menu-type="standard"
            data-select-label="selected"
            style={{ zIndex: dropdownOpen ? 10000 : 'auto' }}
          >
            <div className="ds-dropdown__drop-menu relative">
              <label className="ds-dropdown__label"></label>

              <button
                className="ds-dropdown__trigger-button ds-paginationTrigger"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
                onClick={(e) => {
                  e.preventDefault();
                  setDropdownOpen((open) => !open);
                }}
              >
                <span className="ds-dropdown__trigger-text">
                  Page {currentPage}
                </span>
                <img
                  className="ds-dropdown__chevron"
                  src="/assets/images/chevrondown.svg"
                  width="24"
                  height="24"
                  aria-hidden="true"
                  alt="Toggle dropdown"
                />
              </button>

              <ul
                className="ds-dropdown__menu-list paginationList"
                style={{ 
                  display: dropdownOpen ? "block" : "none", 
                  position: 'absolute', 
                  zIndex: 10000, 
                  maxHeight: '300px', 
                  overflowY: 'auto', 
                  backgroundColor: '#fff', 
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px'
                }}
                role="listbox"
                tabIndex={-1}
              >
                {/* For large datasets, only show nearby pages instead of all pages */}
                {totalPages <= 100 ? (
                  // Show all pages if there are 100 or fewer
                  getPageRange(1, totalPages).map((page) => (
                    <li key={page}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleClick(page);
                        }}
                        className="drop-menu-list-text"
                        role="option"
                        aria-selected={page === currentPage}
                      >
                        Page {page}
                      </button>
                    </li>
                  ))
                ) : (
                  // For large datasets, show nearby pages and quick jump input
                  <>
                    {visiblePages.map((page) => (
                      <li key={page}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleClick(page);
                          }}
                          className="drop-menu-list-text"
                          role="option"
                          aria-selected={page === currentPage}
                        >
                          Page {page}
                        </button>
                      </li>
                    ))}
                    <li className="ds-dropdown__divider my-2" />
                    <li className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        placeholder="Go to page..."
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const pageNum = parseInt((e.target as HTMLInputElement).value);
                            if (pageNum >= 1 && pageNum <= totalPages) {
                              handleClick(pageNum);
                            }
                          }
                        }}
                      />
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* Mobile next */}
          <li
            className={`ds-pagination__item -next ${currentPage === totalPages ? "-disabled" : ""
              }`}
          >
            <button
              className="ds-pagination__link"
              disabled={currentPage === totalPages}
              onClick={() => {
                handleClick(currentPage + 1);
              }}
            >
              <span className="screenreader-only">Next page</span>
              <img
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-forward"
                alt="go to next page"
              />
            </button>
            <span>
              <p className="forward-label mt-[10px]!" aria-hidden="true">
                Next results
              </p>
            </span>
          </li>
        </div>
      </nav>
    </div>
  );
};

export default Pagination;